'use client'

import React, { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'
import { getBackgroundFromShade } from '@/utilities/sectionSettings'
import type { FullWidthMediaBlock } from '@/payload-types'

type BackgroundImage = NonNullable<FullWidthMediaBlock['backgroundImage']>

const ParallaxImage: React.FC<{
  image: BackgroundImage
  alt: string
  enableParallax: boolean
}> = ({ image, alt, enableParallax }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  const y = useTransform(scrollYProgress, [0, 1], [-80, 80])

  return (
    <motion.div
      ref={ref}
      className="absolute inset-0 will-change-transform"
      style={enableParallax ? { y } : {}}
    >
      <Media
        resource={typeof image === 'object' ? image : null}
        alt={alt}
        fill
        priority
        imgClassName="object-cover"
        size="100vw"
      />
    </motion.div>
  )
}

export const FullWidthMediaClient: React.FC<FullWidthMediaBlock> = ({
  backgroundType,
  backgroundImage,
  backgroundVideo,
  infoTag,
  title,
  subtitle,
  overlay,
  height = 'full',
  textAlignment = 'center',
  enableParallax = true,
  settings,
}) => {
  const heightClasses = {
    full: 'h-screen',
    large: 'h-[80vh]',
    medium: 'h-[60vh]',
  }

  const alignmentClasses = {
    left: 'text-center lg:text-left',
    center: 'text-center',
    right: 'text-center lg:text-right',
  }

  const overlayEnabled = overlay?.enabled !== false
  const overlayOpacity = overlay?.opacity || 40
  const overlayColor = overlay?.color || 'black'

  const overlayStyles = overlayEnabled
    ? {
        backgroundColor:
          overlayColor === 'black'
            ? `rgba(0, 0, 0, ${overlayOpacity / 100})`
            : overlayColor === 'white'
              ? `rgba(255, 255, 255, ${overlayOpacity / 100})`
              : `rgba(var(--color-primary), ${overlayOpacity / 100})`,
      }
    : {}

  return (
    <section
      className={cn(
        'relative w-full overflow-hidden',
        heightClasses[height || 'full'],
        settings?.backgroundShade &&
          settings.backgroundShade !== 'white' &&
          `bg-${getBackgroundFromShade(settings.backgroundShade)}`,
        settings?.isDarkMode && 'data-theme-dark',
      )}
    >
      {backgroundType === 'image' && backgroundImage ? (
        <ParallaxImage
          image={backgroundImage}
          alt={title || ''}
          enableParallax={enableParallax !== false}
        />
      ) : backgroundType === 'video' && backgroundVideo ? (
        <div className="absolute inset-0 w-full h-full">
          <Media
            resource={
              backgroundVideo.videoFile && typeof backgroundVideo.videoFile === 'object'
                ? backgroundVideo.videoFile
                : null
            }
            videoUrl={backgroundVideo.videoUrl || undefined}
            poster={
              backgroundVideo.posterImage && typeof backgroundVideo.posterImage === 'object'
                ? backgroundVideo.posterImage
                : null
            }
            videoClassName="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      ) : null}

      {overlayEnabled && <div className="absolute inset-0" style={overlayStyles} />}

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center px-8 max-w-6xl">
          <div className={alignmentClasses[textAlignment || 'center']}>
            {infoTag && (
              <div
                className="mb-4 animate-fade-in-up [--initial-y:24px] opacity-0 [animation-delay:100ms]"
              >
                <div className="inline-block px-3 py-1 bg-brand text-white text-sm font-medium uppercase tracking-wider">
                  {infoTag}
                </div>
              </div>
            )}

            {title && (
              <div
                className="display-md on-dark mb-4 uppercase animate-fade-in-up [--initial-y:28px] opacity-0 [animation-delay:200ms]"
              >
                {title}
              </div>
            )}

            {subtitle && (
              <p
                className="subtitle-lg on-dark-secondary max-w-2xl mx-auto animate-fade-in-up [--initial-y:20px] opacity-0 [animation-delay:350ms]"
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
